import { getLocale } from "./Translation";

const toMonthKey = (year, month) => `${year}-${String(month).padStart(2, "0")}`;

export const getLastCompletedMonth = (now = new Date()) => {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export const capToLastCompletedMonth = (year, month, now = new Date()) => {
  const last = getLastCompletedMonth(now);
  const requested = year * 12 + (month - 1);
  const max = last.year * 12 + (last.month - 1);
  if (requested <= max) return { year, month };
  return last;
};

/** Mois en cours ou futur : pas de données sur les graphs (dernier mois affiché = dernier mois complété). */
export const isFutureMonth = (year, month, now = new Date()) => {
  const last = getLastCompletedMonth(now);
  const requested = year * 12 + (month - 1);
  const maxCompleted = last.year * 12 + (last.month - 1);
  return requested > maxCompleted;
};

const monthToNumber = (ym) => {
  const [y, m] = ym.split("-").map((v) => parseInt(v, 10));
  return y * 12 + (m - 1);
};

const keyToYearMonth = (key) => {
  const year = Math.floor(key / 12);
  const month = (key % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
};

const capFirst = (s) => {
  if (!s || typeof s !== "string") return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatMonthShort = (locale, year, month) => {
  const label = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: "short" });
  const cleaned = String(label).replace(".", "");
  return capFirst(cleaned);
};

/** Pending = ligne monthly_records + toggle Manager désactivé (show_on_platform ≠ 1). */
export const resolvePlatformMonthFlags = (hasMonthlyRecord, showOnPlatform) => {
  const hasRecord = Boolean(hasMonthlyRecord);
  const published = Number(showOnPlatform) === 1;
  return {
    hasMonthlyRecord: hasRecord,
    showOnPlatform: hasRecord ? (published ? 1 : 0) : 0,
    isPlatformMonthPending: hasRecord && !published,
  };
};

const emptyMonthEntry = (month) => ({
  month,
  totalWaterSavedInEuros: 0,
  totalEnergySavedInEuros: 0,
  totalActionsSavedInEuros: 0,
  graphTotalWaterSaved: 0,
  graphTotalEnergySaved: 0,
  totalActionsWaterSavedLiters: 0,
  totalActionsCO2SavedKg: 0,
  totalLuniShowerSavedInEuros: 0,
  isFutureMonth: false,
  hasMonthlyRecord: false,
  showOnPlatform: 0,
  isPlatformMonthPending: false,
});

const applyFinancialChartPlatformRules = (item, hasWaterOption) => {
  const flags = resolvePlatformMonthFlags(item.hasMonthlyRecord, item.showOnPlatform);
  if (!hasWaterOption) {
    return { ...item, ...flags };
  }
  if (flags.isPlatformMonthPending) {
    return {
      ...item,
      ...flags,
      graphTotalWaterSaved: 0,
      graphTotalEnergySaved: 0,
      totalWaterSavedInEuros: 0,
      totalEnergySavedInEuros: 0,
      totalLuniShowerSavedInEuros: 0,
      totalActionsSavedInEuros: 0,
      totalActionsWaterSavedLiters: 0,
      totalActionsCO2SavedKg: 0,
    };
  }
  if (!flags.hasMonthlyRecord) {
    return {
      ...item,
      ...flags,
      graphTotalWaterSaved: 0,
      graphTotalEnergySaved: 0,
      totalWaterSavedInEuros: 0,
      totalEnergySavedInEuros: 0,
      totalLuniShowerSavedInEuros: 0,
      totalActionsSavedInEuros: 0,
      totalActionsWaterSavedLiters: 0,
      totalActionsCO2SavedKg: 0,
    };
  }
  return { ...item, ...flags };
};

const pendingMonthEntry = (monthStr) => ({
  ...emptyMonthEntry(monthStr),
  hasMonthlyRecord: true,
  showOnPlatform: 0,
  isPlatformMonthPending: true,
});

export const fillCalendarYearMonths = (data, year, now = new Date(), chartOptions = {}) => {
  const { platformInstallationMissing = false, hasWaterOption = true } = chartOptions;
  const monthMap = new Map();
  (data || []).forEach((item) => {
    if (item?.month) {
      monthMap.set(String(item.month), item);
    }
  });

  const padded = [];
  for (let month = 1; month <= 12; month += 1) {
    const monthStr = toMonthKey(year, month);
    if (isFutureMonth(year, month, now)) {
      padded.push({ ...emptyMonthEntry(monthStr), isFutureMonth: true });
      continue;
    }
    if (hasWaterOption && platformInstallationMissing) {
      padded.push(pendingMonthEntry(monthStr));
      continue;
    }
    const existing = monthMap.get(monthStr);
    padded.push(
      applyFinancialChartPlatformRules(
        existing
          ? { ...existing, isFutureMonth: false }
          : emptyMonthEntry(monthStr),
        hasWaterOption
      )
    );
  }
  return padded;
};

export const fillMissingMonths = (data, endYear, endMonth) => {
  const monthMap = new Map();
  (data || []).forEach((item) => {
    if (item?.month) {
      monthMap.set(String(item.month), item);
    }
  });

  const lastCompleted = getLastCompletedMonth();
  const lastCompletedKey = monthToNumber(toMonthKey(lastCompleted.year, lastCompleted.month));

  if (!endYear || !endMonth) {
    if (monthMap.size === 0) return [];
    const sortedMonths = Array.from(monthMap.keys()).sort();
    const endKey = Math.min(monthToNumber(sortedMonths[sortedMonths.length - 1]), lastCompletedKey);
    const startKey = Math.max(monthToNumber(sortedMonths[0]), endKey - 11);
    const padded = [];
    for (let key = startKey; key <= endKey; key += 1) {
      const monthStr = keyToYearMonth(key);
      padded.push(monthMap.get(monthStr) || emptyMonthEntry(monthStr));
    }
    return padded;
  }

  const capped = capToLastCompletedMonth(endYear, endMonth);
  const endKey = monthToNumber(toMonthKey(capped.year, capped.month));
  const startKey = endKey - 11;
  const padded = [];

  for (let key = startKey; key <= endKey; key += 1) {
    const monthStr = keyToYearMonth(key);
    padded.push(monthMap.get(monthStr) || emptyMonthEntry(monthStr));
  }

  return padded;
};

export const buildShowerConsumptionCalendarSeries = (
  records = [],
  year,
  locale,
  now = new Date(),
  chartOptions = {}
) => {
  const { platformInstallationMissing = false } = chartOptions;
  const byMonth = new Map((records || []).map((record) => [String(record.month), record]));
  const points = [];

  for (let month = 1; month <= 12; month += 1) {
    const key = toMonthKey(year, month);
    const future = isFutureMonth(year, month, now);
    if (platformInstallationMissing && !future) {
      points.push({
        key,
        monthLabel: formatMonthShort(locale, year, month),
        isFutureMonth: false,
        hasMonthlyRecord: true,
        showOnPlatform: 0,
        isPlatformMonthPending: true,
        consoTemoin: 0,
        consoLunishower: 0,
      });
      continue;
    }
    const record = byMonth.get(key);
    const flags = resolvePlatformMonthFlags(record?.hasMonthlyRecord, record?.showOnPlatform);
    const isPlatformMonthPending = !future && flags.isPlatformMonthPending;
    const showValues = !future && flags.hasMonthlyRecord && !isPlatformMonthPending;

    points.push({
      key,
      monthLabel: formatMonthShort(locale, year, month),
      isFutureMonth: future,
      hasMonthlyRecord: flags.hasMonthlyRecord,
      showOnPlatform: flags.showOnPlatform,
      isPlatformMonthPending,
      consoTemoin: showValues ? Number(record?.consoTemoin) || 0 : 0,
      consoLunishower: showValues ? Number(record?.consoLunishower) || 0 : 0,
    });
  }

  return points;
};

export const buildEconomiesCalendarSeries = (
  records = [],
  year,
  locale,
  now = new Date(),
  chartOptions = {}
) => {
  const { platformInstallationMissing = false } = chartOptions;
  const byMonth = new Map((records || []).map((record) => [String(record.month), record]));
  const points = [];

  for (let month = 1; month <= 12; month += 1) {
    const key = toMonthKey(year, month);
    const future = isFutureMonth(year, month, now);
    if (platformInstallationMissing && !future) {
      points.push({
        key,
        monthLabel: formatMonthShort(locale, year, month),
        isFutureMonth: false,
        hasMonthlyRecord: true,
        showOnPlatform: 0,
        isPlatformMonthPending: true,
        waterSavings: 0,
        energySavings: 0,
      });
      continue;
    }
    const record = byMonth.get(key);
    const flags = resolvePlatformMonthFlags(record?.hasMonthlyRecord, record?.showOnPlatform);
    const isPlatformMonthPending = !future && flags.isPlatformMonthPending;
    const showValues = !future && flags.hasMonthlyRecord && !isPlatformMonthPending;

    points.push({
      key,
      monthLabel: formatMonthShort(locale, year, month),
      isFutureMonth: future,
      hasMonthlyRecord: flags.hasMonthlyRecord,
      showOnPlatform: flags.showOnPlatform,
      isPlatformMonthPending,
      waterSavings: showValues ? Number(record?.waterSavingsM3) || 0 : 0,
      energySavings: showValues ? Number(record?.energySavingsKwh) || 0 : 0,
    });
  }

  return points;
};

export const formatDashboardChartData = (
  data,
  year,
  _endMonth,
  lang = "fr",
  now = new Date(),
  chartOptions = {}
) => {
  const filled = year
    ? fillCalendarYearMonths(data || [], year, now, chartOptions)
    : fillMissingMonths(data || [], null, null);
  const locale = getLocale(lang);
  return filled.map((item) => {
    const rawMonth = item.month;
    const [y, m] = item.month.split("-");
    const date = new Date(`${y}-${m}-01`);
    const shortMonth = date.toLocaleDateString(locale, { month: "short" }).replace(".", "");
    const formattedDate = capFirst(shortMonth);
    return {
      ...item,
      monthRaw: rawMonth,
      month: formattedDate,
      key: toMonthKey(parseInt(y, 10), parseInt(m, 10)),
      isFutureMonth: Boolean(item.isFutureMonth),
    };
  });
};
